import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { usePageTitle } from "@/lib/content";
import { Reveal } from "@/components/site/Reveal";

const CANON_BASE = "https://sitebound.com.au";

function JsonLd({ data }) {
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify(data);
    document.head.appendChild(el);
    return () => el.remove();
  }, [data]);
  return null;
}

const UNKNOWN_POINTS = [
  "How their resume should look",
  "Which tickets and licences they need",
  "Where to find the right jobs",
  "How to approach applications",
  "What to expect in an interview",
  "What working in construction or FIFO is actually like",
  "What to look for when they're offered a job",
];

export default function About() {
  usePageTitle(
    "About Sitebound | Construction & FIFO Job Seeker Resources",
    "Meet Jack, the founder of Sitebound. 1,000+ successful job placements and a top 10 SEEK recruiter ranking in Western Australia, turned into practical construction, civil, mining and FIFO job seeker resources."
  );

  const profileLd = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    mainEntity: {
      "@type": "Person",
      name: "Jack",
      jobTitle: "Founder",
      image: `${CANON_BASE}/jack-founder.jpg`,
      worksFor: { "@type": "Organization", name: "Sitebound", url: CANON_BASE },
    },
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${CANON_BASE}/` },
      { "@type": "ListItem", position: 2, name: "About", item: `${CANON_BASE}/about` },
    ],
  };

  return (
    <article data-testid="about-page" className="mx-auto max-w-3xl px-4 py-14 sm:px-6 md:py-20">
      <JsonLd data={profileLd} />
      <JsonLd data={breadcrumbLd} />

      <nav aria-label="Breadcrumb" className="font-mono-ind text-[11px] uppercase tracking-[0.2em] text-slate-500">
        <Link to="/" className="transition-colors hover:text-[#FF5500]">Home</Link>
        <span className="mx-2 text-slate-600">/</span>
        <span className="text-slate-400">About</span>
      </nav>

      <Reveal>
        <header className="mt-8 border-b border-white/10 pb-10">
          <div className="mono-label">About Sitebound</div>
          <h1 className="font-display mt-4 text-3xl font-extrabold uppercase leading-[1.02] tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
            Built from real recruitment experience
          </h1>
        </header>
      </Reveal>

      <Reveal>
        <div className="mt-10 flex flex-col gap-8 sm:flex-row sm:items-start">
          <img
            src="/jack-founder.jpg"
            alt="Jack, founder of Sitebound"
            data-testid="about-founder-photo"
            width="900"
            height="900"
            className="mx-auto w-44 shrink-0 rounded-sm border border-white/10 sm:mx-0 sm:w-56"
          />
          <div>
            <p className="text-base font-semibold leading-relaxed text-slate-100">
              Hi, I'm Jack, the founder of Sitebound.
            </p>
            <p className="mt-4 text-[15px] leading-relaxed text-slate-300">
              I've spent years working in civil and construction recruitment, helping people find
              opportunities across the Australian construction and civil industries.
            </p>
            <p className="mt-4 text-[15px] leading-relaxed text-slate-300">
              Throughout my recruitment career, I've made 1,000+ successful job placements and I'm
              currently ranked among the top 10 recruiters in Western Australia on SEEK.
            </p>
          </div>
        </div>
      </Reveal>

      <Reveal>
        <p className="mt-8 text-[15px] leading-relaxed text-slate-300">
          Working in recruitment has given me a clear understanding of what happens on both sides of
          the application process. I've seen where candidates commonly get stuck and how difficult
          it can be for someone trying to get their foot in the door.
        </p>
        <p className="mt-4 text-[15px] leading-relaxed text-slate-300">
          I've seen plenty of people who want to get into construction, civil, mining or FIFO work
          but don't know:
        </p>
        <ul className="mt-5 space-y-2.5">
          {UNKNOWN_POINTS.map((point) => (
            <li key={point} className="flex items-start gap-3 text-[15px] leading-relaxed text-slate-300">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 bg-[#FF5500]" />
              {point}
            </li>
          ))}
        </ul>
      </Reveal>

      <Reveal>
        <p className="font-display mt-12 text-2xl font-bold uppercase tracking-tight text-slate-100 sm:text-3xl">
          That's why I created <span className="text-[#FF5500]">Sitebound.</span>
        </p>
        <p className="mt-5 text-[15px] leading-relaxed text-slate-300">
          I wanted to take the knowledge and practical information I've built up through recruitment
          and turn it into something people can actually use.
        </p>
        <p className="mt-4 text-[15px] leading-relaxed text-slate-300">
          Sitebound brings together 95 practical resources covering resumes, applications, tickets
          and licences, interviews, construction basics, FIFO, pay, job offers, checklists and more.
        </p>
        <p className="mt-4 text-[15px] leading-relaxed text-slate-300">
          Whether you're completely new to the industry or looking to take the next step, the goal
          is simple:
        </p>
        <p className="font-display mt-6 text-2xl font-extrabold uppercase tracking-tight text-[#FF5500] sm:text-3xl">
          Get ready. Get hired. Get on site.
        </p>
        <div className="mt-10">
          <p className="text-lg font-semibold text-slate-100">Jack</p>
          <p className="font-mono-ind mt-1 text-[11px] uppercase tracking-[0.2em] text-slate-500">
            Founder, Sitebound
          </p>
        </div>
      </Reveal>

      <Reveal>
        <section data-testid="about-cta" className="mt-14 border border-[#FF5500]/30 bg-[#0D0F12] p-8">
          <div className="mono-label">The Complete Pack</div>
          <p className="mt-4 text-sm leading-relaxed text-slate-300">
            95 practical resources across 10 sections, all readable online in your Job Seeker Hub
            with editable templates to download.
          </p>
          <Link
            data-testid="about-cta-pack"
            to="/product/complete-construction-job-seeker-pack"
            className="mt-6 inline-flex items-center gap-2 bg-[#FF5500] px-6 py-3 text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E04800]"
          >
            Explore the Complete Construction Job Seeker Pack <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </section>
      </Reveal>

      <Reveal>
        <section data-testid="about-contact" className="mt-14 border-t border-white/10 pt-10">
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-slate-100">
            Have any questions?
          </h2>
          <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-slate-300">
            If you have any questions about Sitebound, the Complete Construction Job Seeker Pack, or
            what's included, feel free to get in touch.
          </p>
          <p className="mt-4 text-sm text-slate-400">
            Email us at:{" "}
            <a
              data-testid="about-email-link"
              href="mailto:hello@sitebound.com.au"
              className="text-[#FF5500] underline underline-offset-2 transition-colors hover:text-white"
            >
              hello@sitebound.com.au
            </a>
          </p>
          <Link
            data-testid="about-contact-button"
            to="/contact"
            className="mt-6 inline-flex items-center gap-2 border border-white/25 px-6 py-3 text-xs font-bold uppercase tracking-widest text-slate-100 transition-colors hover:border-[#FF5500] hover:text-[#FF5500]"
          >
            Contact Sitebound
          </Link>
        </section>
      </Reveal>
    </article>
  );
}
